/**
 * Customer Portal (UI) - event assignment create/edit modal.
 */
import type { Dispatch, FormEvent, SetStateAction } from 'react';
import type { CallbackProfile, ScopeType, WebhookTypeId } from '../../types/portal';
import type { EventAssignmentDraft } from './webhookDrafts';
import { profileDisplayName } from './webhookDrafts';

type EventAssignmentModalProps = {
  isOpen: boolean;
  accountId: string | null;
  saving: boolean;
  draft: EventAssignmentDraft;
  profiles: CallbackProfile[];
  webhookTypeOptions: Array<{ value: string; label: string }>;
  scopeOptions: Array<{ id: string; label: string }>;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  setDraft: Dispatch<SetStateAction<EventAssignmentDraft>>;
};

export function EventAssignmentModal({
  isOpen,
  accountId,
  saving,
  draft,
  profiles,
  webhookTypeOptions,
  scopeOptions,
  onClose,
  onSubmit,
  setDraft
}: EventAssignmentModalProps) {
  if (!isOpen) {
    return null;
  }

  const isEditing = Boolean(draft.assignmentId);

  return (
    <div className="modal modal-blur fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }} role="dialog" aria-modal="true">
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <form onSubmit={onSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">{isEditing ? 'Edit Event Assignment' : 'Create Event Assignment'}</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} disabled={saving} />
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Webhook Type</label>
                  <select
                    className="form-select"
                    value={draft.webhookType}
                    onChange={(event) => setDraft((current) => ({ ...current, webhookType: event.target.value as WebhookTypeId }))}
                  >
                    {webhookTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Scope Type</label>
                  <select
                    className="form-select"
                    value={draft.scopeType}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        scopeType: event.target.value as ScopeType,
                        scopeId: event.target.value === 'ACCOUNT' ? accountId ?? '' : ''
                      }))
                    }
                  >
                    <option value="ACCOUNT">ACCOUNT</option>
                    <option value="BRAND">BRAND</option>
                    <option value="CAMPAIGN">CAMPAIGN</option>
                    <option value="PHONE">PHONE</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Scope</label>
                  <select
                    className="form-select"
                    value={draft.scopeId}
                    onChange={(event) => setDraft((current) => ({ ...current, scopeId: event.target.value }))}
                    required
                  >
                    <option value="" disabled>
                      Select scope
                    </option>
                    {scopeOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Callback Profile</label>
                  <select
                    className="form-select"
                    value={draft.profileId}
                    onChange={(event) => setDraft((current) => ({ ...current, profileId: event.target.value }))}
                    required
                  >
                    <option value="" disabled>
                      Select profile
                    </option>
                    {profiles.map((profile) => (
                      <option key={profile.profileId} value={profile.profileId}>
                        {profileDisplayName(profile)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Priority</label>
                  <input
                    type="number"
                    className="form-control"
                    value={draft.priority}
                    onChange={(event) =>
                      setDraft((current) => ({
                        ...current,
                        priority: Number.isFinite(Number(event.target.value)) ? Number(event.target.value) : 0
                      }))
                    }
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Enabled</label>
                  <label className="form-check form-switch mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={draft.enabled}
                      onChange={(event) => setDraft((current) => ({ ...current, enabled: event.target.checked }))}
                    />
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-link link-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary ms-auto" disabled={saving || !accountId}>
                {saving ? 'Saving...' : isEditing ? 'Update Assignment' : 'Create Assignment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
